import React, { useRef } from 'react';
import { useDialogFocus } from '../Help/useDialogFocus';
import { Link, matchPath, useLocation } from 'react-router-dom';
import {
  Home,
  Droplets,
  X,
  Building2,
  ClipboardList,
  FileText,
  FlaskConical,
  User,
  Settings,
  CheckSquare,
  Users,
  MapPin,
  UserPlus,
  Bell,
  Tags,
  FileCheck,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useViewMode } from '../../contexts/ViewModeContext';
import './Sidebar.css';

interface NavItem {
  path: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  label: string;
  color: string;
  end?: boolean;
  title?: string;
}

const CONSULTANT_NAV = (isFacilityView: boolean): NavItem[] => [
  { path: '/', icon: Home, label: 'Dashboard', color: '#0077be', end: true },
  isFacilityView
    ? { path: '/facilities', icon: MapPin, label: 'Facilities', color: '#0077be' }
    : { path: '/clients', icon: Users, label: 'Clients', color: '#0077be' },
  { path: '/actions', icon: CheckSquare, label: 'Actions', color: '#ef5350' },
  { path: '/notifications', icon: Bell, label: 'Notifications', color: '#f59e0b' },
  { path: '/lab-reports', icon: FlaskConical, label: 'Lab reports', color: '#7c3aed' },
  { path: '/reports', icon: FileText, label: 'Reports', color: '#66bb6a' },
  { path: '/invites', icon: UserPlus, label: 'Invite users', color: '#26a69a' },
  { path: '/profile', icon: User, label: 'Profile', color: '#5c6bc0' },
  { path: '/settings', icon: Settings, label: 'Settings', color: '#78909c' },
];

const PORTAL_NAV: NavItem[] = [
  { path: '/portal', icon: Home, label: 'Dashboard', color: '#0077be', end: true },
  { path: '/portal/facilities', icon: Building2, label: 'Facilities', color: '#29b6f6' },
  { path: '/portal/actions', icon: CheckSquare, label: 'Actions', color: '#ef5350' },
  { path: '/portal/notifications', icon: Bell, label: 'Notifications', color: '#f59e0b' },
  { path: '/portal/inspections', icon: ClipboardList, label: 'Inspections', color: '#66bb6a' },
  { path: '/portal/lab-reports', icon: FlaskConical, label: 'Lab results', color: '#7c3aed' },
  { path: '/portal/wrmp', icon: FileCheck, label: 'Plan', title: 'Risk management plan', color: '#00897b' },
  { path: '/portal/profile', icon: User, label: 'Profile', color: '#5c6bc0' },
];

const ADMIN_NAV: NavItem[] = [
  { path: '/admin/feedback', icon: FileText, label: 'Feedback', color: '#0077be' },
  { path: '/admin', icon: Building2, label: 'Consultancies', color: '#0077be', end: true },
  { path: '/admin/asset-types', icon: Tags, label: 'Asset library', color: '#7c3aed' },
  { path: '/admin/invites', icon: UserPlus, label: 'Invites', color: '#26a69a' },
  { path: '/admin/profile', icon: User, label: 'Profile', color: '#5c6bc0' },
];

/** Resolve the navigation for the current role and preview mode. */
export function useNavigation() {
  const { currentUser, isH2okayAdmin } = useAuth();
  const { effectiveRole } = useImpersonation();
  const { isFacilityView } = useViewMode();
  const location = useLocation();
  // When admin is acting-as another user, navigation switches to the
  // role-appropriate nav so the admin sees what the impersonated user
  // sees. ``effectiveRole`` is non-null only while impersonating.
  const isImpersonating = effectiveRole !== null;
  const impersonatingPortalRole =
    effectiveRole === 'client' || effectiveRole === 'facility_manager';
  const isAdmin =
    !isImpersonating &&
    (isH2okayAdmin || location.pathname.startsWith('/admin'));
  const isPortal =
    impersonatingPortalRole ||
    (!isAdmin &&
      !isImpersonating &&
      (location.pathname.startsWith('/portal') ||
        currentUser?.role === 'client' ||
        currentUser?.role === 'facility_manager'));
  let navItems: NavItem[];
  if (isAdmin) {
    navItems = ADMIN_NAV;
  } else if (isPortal) {
    navItems = PORTAL_NAV;
  } else {
    navItems = CONSULTANT_NAV(isFacilityView);
  }


  const extraPaths = isPortal ? ['/portal/profile','/portal/notifications'] : isAdmin ? ['/admin/profile'] : ['/profile','/settings','/notifications','/invites'];
  const mobileItems = navItems.filter(item => extraPaths.includes(item.path));
  navItems = navItems.filter(item => !extraPaths.includes(item.path) && (isPortal || isAdmin || item.path !== '/inspections'));
  return { navItems, mobileItems, badge: isPortal ? 'Client view' : isAdmin ? 'Admin view' : isFacilityView ? 'Facility view' : '' };
}

/** Render the same labelled routes in tabs, the drawer or the bottom bar. */
export function Navigation({ items, label, className, onNavigate }: {
  items: NavItem[]; label: string; className?: string; onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  return <nav aria-label={label} className={className}>
    {items.map(item => {
      const paths = item.path === '/clients' ? ['/clients', '/facilities'] : [item.path];
      const active = paths.some(path => matchPath({ path, end: item.end ?? false }, pathname));
      return <Link key={item.path} to={item.path}
        title={item.title} onClick={onNavigate} aria-current={active ? 'page' : undefined}
        data-tour={'nav-' + (item.path === '/' ? 'dashboard' : item.path.slice(1).replaceAll('/', '-'))}
        className={`nav-link ${active ? 'active' : ''}`}>
        <item.icon size={20} aria-hidden="true" /><span>{item.label}</span>
      </Link>;
    })}
  </nav>;
}

/** Display the mobile drawer, including secondary account destinations. */
export default function Sidebar({ onNavigate }: { onNavigate: () => void }) {
  const { navItems, mobileItems } = useNavigation();
  const drawer = useRef<HTMLElement>(null);
  useDialogFocus(drawer);
  return <aside ref={drawer} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Navigation menu" id="navigation-menu" className="sidebar">
    <div className="drawer-header">
      <div className="drawer-logo"><Droplets size={24} aria-hidden="true" /><span>H2Okay</span></div>
      <button className="menu-btn" onClick={onNavigate} aria-label="Close menu"><X size={20} aria-hidden="true" /></button>
    </div>
    <nav aria-label="Mobile navigation">
      <Navigation items={navItems} label="Work areas" onNavigate={onNavigate} />
      {mobileItems.length > 0 && <>
        <hr className="drawer-divider" />
        <Navigation items={mobileItems} label="Account navigation" onNavigate={onNavigate} />
      </>}
    </nav>
  </aside>;
}
